import { useState, useEffect, useMemo, useCallback } from 'react';
import { queryService } from '../../services/queryService';
import { useSnackbar } from '../../components/common/Snackbar';

/**
 * Hook to manage query status update state and logic
 * @param {Object} query - Current query object
 * @param {string} queryId - Query ID
 * @param {boolean} canUpdateStatus - Whether user can update status
 * @param {function} onQueryUpdate - Callback when query is updated
 * @param {function} onTimelineFetch - Callback to fetch timeline
 * @returns {Object} Status update state and handlers
 */
export const useQueryStatusUpdate = (query, queryId, canUpdateStatus, onQueryUpdate, onTimelineFetch) => {
  const { showSnackbar } = useSnackbar();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusError, setStatusError] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const baseStatusOptions = useMemo(
    () => ['submitted', 'reopened', 'additional_clarification'],
    []
  );

  const statusOptions = useMemo(() => {
    let options = [...baseStatusOptions];
    
    // Get current status from query
    const currentStatus = query ? (query.statusLabel || query.status || '').toString().trim().toLowerCase() : '';
    
    // If current status is "submitted", show "Need Clarification" (reopened) instead of "additional_clarification"
    // (backend will auto-convert reopened submitted queries to additional_clarification)
    if (currentStatus === 'submitted') {
      options = options.filter(opt => opt !== 'additional_clarification');
      if (!options.includes('reopened')) {
        options.push('reopened');
      }
    }
    
    // Always include the current status if it's not in the list
    if (selectedStatus && !options.includes(selectedStatus)) {
      options = [selectedStatus, ...options];
    }
    
    return options;
  }, [baseStatusOptions, selectedStatus, query]);

  const normalizedCurrentStatus = useMemo(() => {
    if (!query) return '';
    const current = (query.statusLabel || query.status || '').toString().trim().toLowerCase();
    return current;
  }, [query]);

  // Update selectedStatus when query changes
  useEffect(() => {
    if (query) {
      const normalizedStatus = (query.statusLabel || query.status || '')
        .toString()
        .trim()
        .toLowerCase();
      setSelectedStatus(normalizedStatus);
    } else {
      setSelectedStatus('');
    }
  }, [query]);

  const handleStatusUpdate = useCallback(async (event) => {
    event.preventDefault();

    if (!canUpdateStatus) {
      showSnackbar('Only the assignee, creator, or assignor can update the status.', 'warning');
      return;
    }

    const normalizedSelection = selectedStatus.toString().trim().toLowerCase();

    if (!normalizedSelection) {
      setStatusError('Please select a status to update.');
      return;
    }

    if (normalizedSelection === normalizedCurrentStatus) {
      showSnackbar('Status is already up to date.', 'info');
      return;
    }

    setStatusError('');
    setIsUpdatingStatus(true);

    try {
      // If user selects "additional_clarification" and current status is "submitted",
      // send "reopened" as status and let backend set statusLabel to "additional_clarification"
      let statusToSend = normalizedSelection;
      let statusLabelToSend = normalizedSelection;
      
      if (normalizedSelection === 'additional_clarification' && normalizedCurrentStatus === 'submitted') {
        statusToSend = 'reopened';
        // Backend will automatically set statusLabel to 'additional_clarification' when reopening submitted
        statusLabelToSend = 'reopened';
      } else if (normalizedSelection === 'reopened' && normalizedCurrentStatus === 'submitted') {
        // If user selects "reopened" from submitted, backend will auto-convert to additional_clarification
        statusToSend = 'reopened';
        statusLabelToSend = 'reopened';
      }

      const payload = {
        status: statusToSend,
        statusLabel: statusLabelToSend,
      };

      const updatedQuery = await queryService.updateQueryStatus(queryId, payload);

      if (updatedQuery) {
        if (onQueryUpdate) {
          onQueryUpdate(updatedQuery);
        }
      } else {
        // If update doesn't return query, refetch
        if (onQueryUpdate) {
          // Trigger refetch
          onQueryUpdate(null);
        }
      }

      if (onTimelineFetch) {
        onTimelineFetch({ silent: true });
      }
      
      showSnackbar('Status updated successfully', 'success');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to update the status. Please try again.';
      showSnackbar(message, 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [selectedStatus, normalizedCurrentStatus, canUpdateStatus, queryId, showSnackbar, onQueryUpdate, onTimelineFetch]);

  return {
    selectedStatus,
    setSelectedStatus,
    statusError,
    setStatusError,
    isUpdatingStatus,
    statusOptions,
    normalizedCurrentStatus,
    handleStatusUpdate,
  };
};
